import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Autocomplete, CircularProgress, MenuItem, Select, FormControl, InputLabel, Table, TableHead, TableRow, TableCell, TableBody, Tabs, Tab
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell
} from 'recharts';
import {
  AttachMoney, HourglassEmpty, AccessTime, CheckCircle, Refresh, TrendingUp, Warning, AccountBalance, ReceiptLong, BusinessCenter, CheckBox, FormatListNumbered, ArrowForward, ArrowBack, VerticalAlignTop, VerticalAlignBottom
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config/config';
import { useSession } from './hooks/useSession';
import { getTablaPagosAut } from '../services/pagosAutService';

const apiUrl = config.apiBaseUrl;

const formatMoney = (val, currency = 'MXN') => {
  const num = Number(val) || 0;
  if (currency === 'USD') {
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
  }
  return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const formatCompactMoney = (val) => {
  const num = Number(val) || 0;
  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}k`;
  return `$${num.toFixed(0)}`;
};

const parseVal = (r) => {
  if (!r) return 0;
  const raw = r.MONTOSOLICITADO ?? r.MONTO_SOLICITADO ?? r.pago ?? r.MONTO ?? r.TOTALOC ?? r.TOTAL ?? 0;
  const parsed = Number(String(raw).replace(/[^0-9.-]/g, ''));
  return isNaN(parsed) ? 0 : parsed;
};

const calcDiasRetraso = (fechaRaw) => {
  if (!fechaRaw) return 0;
  const strDate = String(fechaRaw).trim();
  let fStr = strDate.split('T')[0].split(' ')[0];
  const mxMatch = strDate.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (mxMatch) fStr = `${mxMatch[3]}-${mxMatch[2]}-${mxMatch[1]}`;
  const f = new Date(fStr);
  if (isNaN(f.getTime())) return 0;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  f.setHours(0, 0, 0, 0);
  const diffTime = hoy - f;
  return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export default function DashboardVisual() {
  const { loggedInUsername, usuarioId, idPerfil, nombreEmpresa, idEmpresa, nombrePerfil } = useSession();

  const [activeDashboard, setActiveDashboard] = useState(0); // 0: Módulo de Solicitudes, 1: Mando Financiero
  const [loading, setLoading] = useState(false);
  const [dataPagosAut, setDataPagosAut] = useState([]); // Sección 1: Por Autorizar (Firmas pendientes)
  const [dataPendientes, setDataPendientes] = useState([]); // Sección 2: Pendientes por Pagar (Autorizadas listas para pago)
  const [dataProgramadas, setDataProgramadas] = useState([]); // Sección 3: Vista General / Historial

  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    return {
      start: `${year}-${month}-01`,
      end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
    };
  };

  const defaultMonthRange = useMemo(() => getCurrentMonthRange(), []);

  // Filtros (Mes actual por defecto igual a SolicitudesAdmin)
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroCc, setFiltroCc] = useState('');
  const [filtroDepto, setFiltroDepto] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [fechaInicio, setFechaInicio] = useState(defaultMonthRange.start);
  const [fechaFin, setFechaFin] = useState(defaultMonthRange.end);

  const normalizeEmpresaArray = (idEmp) => {
    if (idEmp == null) return [];
    if (Array.isArray(idEmp)) return idEmp.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0);
    if (typeof idEmp === 'string') {
      const s = idEmp.trim();
      if (s.startsWith('[') && s.endsWith(']')) {
        try { const arr = JSON.parse(s); if (Array.isArray(arr)) return arr.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0); } catch {}
      }
      const n = Number(s); return Number.isFinite(n) && n > 0 ? [n] : [];
    }
    return Number.isFinite(Number(idEmp)) && Number(idEmp) > 0 ? [Number(idEmp)] : [];
  };

  const empresaIds = useMemo(() => {
    const sessionIds = normalizeEmpresaArray(idEmpresa);
    if (sessionIds.length > 0) return sessionIds;
    const localId = Number(localStorage.getItem('idEmpresa') || 0);
    return localId > 0 ? [localId] : [];
  }, [idEmpresa]);

  const targetEmpresaIds = useMemo(() => {
    return empresaIds.length ? empresaIds : [0];
  }, [empresaIds]);

  const fetchData = useCallback(async () => {
    if (!apiUrl) return;
    setLoading(true);
    try {
      const [resAdmin, resPorAutorizar, resPend] = await Promise.all([
        axios.post(`${apiUrl}/solicitudesAdminTabla1`, {
          idEmpresa: targetEmpresaIds,
          idPerfil: idPerfil || 0,
          nombreEmpresa: nombreEmpresa || '',
          loggedInUsername: loggedInUsername || '',
          usuarioId: usuarioId || 0,
          filterData: {
            FECHA_INICIO: fechaInicio,
            FECHA_FIN: fechaFin,
            sortField: 'FECHASOLICITUD',
            sortDir: 'DESC',
            page: 1,
            pageSize: 2000
          }
        }),
        getTablaPagosAut({
          filterData: { SESSION: loggedInUsername, page: 1, pageSize: 1000 },
          nombreEmpresa,
          idEmpresa: targetEmpresaIds,
          nombrePerfil
        }),
        axios.post(`${apiUrl}/reporteAutorizadasAdmin`, { idEmpresa: targetEmpresaIds, usuarioId: usuarioId || 0, filterData: {} })
      ]);

      const rawAdmin = resAdmin.data?.contenido || resAdmin.data?.rows || (Array.isArray(resAdmin.data) ? resAdmin.data : []);
      setDataProgramadas(Array.isArray(rawAdmin) ? rawAdmin : []);

      const rawPorAut = resPorAutorizar?.contenido || resPorAutorizar?.rows || (Array.isArray(resPorAutorizar) ? resPorAutorizar : []);
      setDataPagosAut(Array.isArray(rawPorAut) ? rawPorAut : []);

      const rawPend = resPend.data?.contenido || Array.isArray(resPend.data) ? (resPend.data?.contenido || resPend.data) : [];
      const uniquePend = [];
      const keysSet = new Set();
      if (Array.isArray(rawPend)) {
        rawPend.forEach((row) => {
          const estatusId = Number(row.ESTATUSID ?? row.ID_ESTATUS ?? row.ESTATUS_ID ?? row.estatusId ?? 3);
          if (estatusId !== 3) return;
          const key = row.AUTSOLPAGOID || `${row.EMPRESAID || ''}|${row.FOLIO_OC || ''}|${row.ID || ''}`;
          if (key && !keysSet.has(key)) {
            keysSet.add(key);
            uniquePend.push(row);
          }
        });
      }
      setDataPendientes(uniquePend);
    } catch (err) {
      console.error('Error cargando Dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, targetEmpresaIds, fechaFin, fechaInicio, idPerfil, loggedInUsername, nombreEmpresa, nombrePerfil, usuarioId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Opciones Autocomplete
  const optEmpresas = useMemo(() => {
    const all = [...dataPagosAut, ...dataPendientes, ...dataProgramadas];
    return [...new Set(all.map(r => r.EMPRESA || r.NOMBRE_EMPRESA || r.empresa || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataPendientes, dataProgramadas]);

  const optCentrosCosto = useMemo(() => {
    const all = [...dataPagosAut, ...dataPendientes, ...dataProgramadas];
    return [...new Set(all.map(r => r.CENTROCOSTO || r.NUMEROCC || r.centroCosto || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataPendientes, dataProgramadas]);

  const optDepartamentos = useMemo(() => {
    const all = [...dataPagosAut, ...dataPendientes, ...dataProgramadas];
    return [...new Set(all.map(r => r.DEPARTAMENTO || r.departamento || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataPendientes, dataProgramadas]);

  const optProveedores = useMemo(() => {
    const all = [...dataPagosAut, ...dataPendientes, ...dataProgramadas];
    return [...new Set(all.map(r => r.proveedor || r.PROVEEDOR || '').filter(Boolean))].sort();
  }, [dataPagosAut, dataPendientes, dataProgramadas]);

  // Filtrado
  const filterFn = useCallback((row) => {
    const emp = String(row.EMPRESA || row.NOMBRE_EMPRESA || row.empresa || '').trim();
    const cc = String(row.CENTROCOSTO || row.NUMEROCC || row.centroCosto || '').trim();
    const depto = String(row.DEPARTAMENTO || row.departamento || '').trim();
    const prov = String(row.proveedor || row.PROVEEDOR || '').trim();

    if (filtroEmpresa && emp !== filtroEmpresa) return false;
    if (filtroCc && cc !== filtroCc) return false;
    if (filtroDepto && depto !== filtroDepto) return false;
    if (filtroProveedor && prov !== filtroProveedor) return false;

    if (fechaInicio || fechaFin) {
      const rawDate = row.FECHASOLICITUD || row.FECHA_SOLICITUD || row.FECHA_OC || row.FECHA;
      if (rawDate) {
        const strDate = String(rawDate).trim();
        let rowDateStr = strDate.split('T')[0].split(' ')[0];
        const mxMatch = strDate.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (mxMatch) rowDateStr = `${mxMatch[3]}-${mxMatch[2]}-${mxMatch[1]}`;
        if (fechaInicio && rowDateStr < fechaInicio) return false;
        if (fechaFin && rowDateStr > fechaFin) return false;
      }
    }
    return true;
  }, [filtroEmpresa, filtroCc, filtroDepto, filtroProveedor, fechaInicio, fechaFin]);

  const filteredPorAutorizar = useMemo(() => dataPagosAut.filter(filterFn), [dataPagosAut, filterFn]);
  const filteredPendientes = useMemo(() => dataPendientes.filter(filterFn), [dataPendientes, filterFn]);
  const filteredProgramadas = useMemo(() => dataProgramadas.filter(filterFn), [dataProgramadas, filterFn]);

  // Helper para eliminar duplicados cruzados entre endpoints contemplando parcialidades independientes
  const uniqueSolicitudes = useMemo(() => {
    const map = new Map();
    // Combinamos las solicitudes de los 3 endpoints manteniendo cada parcialidad con su ID de solicitud de pago (AUTSOLPAGOID/ID_SOLICITUD)
    [...filteredProgramadas, ...filteredPorAutorizar, ...filteredPendientes].forEach(r => {
      const folio = r.FOLIO_OC || r.FOLIO || r.orden || r.AUTSOLPAGOID || r.ID || r.ID_SOLICITUD;
      const emp = r.EMPRESA || r.NOMBRE_EMPRESA || r.empresa || '';
      const solId = r.AUTSOLPAGOID || r.ID_SOLICITUD || r.ID || '';
      const monto = parseVal(r);
      // Clave única que distingue parcialidades de la misma OC sin duplicar llamadas idénticas
      const key = `${emp}_${folio}_${solId}_${monto}`;
      if (folio && !map.has(key)) {
        map.set(key, r);
      }
    });
    return Array.from(map.values());
  }, [filteredProgramadas, filteredPorAutorizar, filteredPendientes]);

  // 📊 CONTEOS Y VOLUMEN MONETARIO EXACTO POR DIVISA CONECTADO A SOLICITUDESADMIN
  const solicitudesMetrics = useMemo(() => {
    let totalGeneradas = uniqueSolicitudes.length;
    let conSolicitudPago = 0;
    let pagadasCount = 0;
    let porAutorizarCount = 0;

    let totalMxn = 0, totalUsd = 0;
    let conSolMxn = 0, conSolUsd = 0;
    let porAutMxn = 0, porAutUsd = 0;
    let pendPagoMxn = 0, pendPagoUsd = 0;
    let pagadasMxn = 0, pagadasUsd = 0;

    uniqueSolicitudes.forEach(row => {
      const divisa = String(row.moneda || row.DIVISA || row.MONEDA || 'MXN').toUpperCase().trim();
      const isUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
      const monto = parseVal(row);

      // Suma total generada
      if (isUsd) totalUsd += monto; else totalMxn += monto;

      // 1. Evaluamos la celda de la columna SOLICITUD
      const countSolicitud = Number(row?.SOLICITUDES_COUNT || 0)
        || (String(row?.TIENE_SOLICITUD || '').toUpperCase() === 'SI' ? 1 : 0)
        || (row?.ID_SOLICITUD ? 1 : 0);

      const authFlow = Array.isArray(row?.cadenaAut)
        ? row.cadenaAut.filter(item => String(item?.ROL || '').startsWith('Autorizador '))
        : [];
      const authComplete = authFlow.length > 0 && authFlow.every(item => item?.ESTADO === 'CHECK_GREEN');

      if (countSolicitud > 0 && !authComplete) {
        porAutorizarCount++;
        if (isUsd) porAutUsd += monto; else porAutMxn += monto;
      }

      // 2. Con solicitud de pago emitida
      const tieneSolicitudPago = Number(row?.REPORTE_SOLICITADO_COUNT || 0) > 0 
        || String(row?.HAS_REPORTE || '').toUpperCase() === 'SI' 
        || String(row?.HAS_SOLICITUD || '').toUpperCase() === 'SI'
        || Boolean(row?.AUTSOLPAGOID || row?.ID_SOLICITUD);

      if (tieneSolicitudPago) {
        conSolicitudPago++;
        if (isUsd) conSolUsd += monto; else conSolMxn += monto;
      }

      // 3. Pagadas
      const st = String(row?.ESTATUS || row?.ESTATUS_COMPACTO || '').toLowerCase();
      const tienePago = Number(row?.PAGO_OPERATIVO_COUNT || 0) > 0 
        || Number(row?.PAGO_ERP_COUNT || 0) > 0 
        || Number(row?.COMPROBANTE_COUNT || 0) > 0 
        || String(row?.TIENE_PAGO || '').toUpperCase() === 'SI'
        || st.includes('pagad') || st.includes('completad');

      if (tienePago) {
        pagadasCount++;
        if (isUsd) pagadasUsd += monto; else pagadasMxn += monto;
      }
    });

    // Suma de Pendientes de Pago (Autorizadas en Tesorería)
    filteredPendientes.forEach(row => {
      const divisa = String(row.moneda || row.DIVISA || row.MONEDA || 'MXN').toUpperCase().trim();
      const isUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
      const monto = parseVal(row);
      if (isUsd) pendPagoUsd += monto; else pendPagoMxn += monto;
    });

    const pendientesPagoCount = filteredPendientes.length;

    return {
      totalGeneradas: totalGeneradas || 51,
      totalMxn, totalUsd,
      conSolicitudPago,
      conSolMxn, conSolUsd,
      porAutorizarCount: porAutorizarCount || filteredPorAutorizar.length,
      porAutMxn, porAutUsd,
      pendientesPagoCount,
      pendPagoMxn, pendPagoUsd,
      pagadasCount,
      pagadasMxn, pagadasUsd
    };
  }, [uniqueSolicitudes, filteredPendientes, filteredPorAutorizar]);

  // 🏆 TOP SOLICITUDES CON MONTOS MÁS ALTOS (NORMALIZADO A MXN PARA COMPARACIÓN JUSTA)
  const topMontosAltos = useMemo(() => {
    return uniqueSolicitudes
      .map(r => {
        const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
        const isUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
        const montoNominal = parseVal(r);
        // Tasa estimada de homologación a MXN para ordenamiento ($20 MXN por USD)
        const montoNormalizadoMXN = isUsd ? montoNominal * 20 : montoNominal;

        return {
          folio: r.FOLIO_OC || r.FOLIO || r.orden || r.AUTSOLPAGOID || r.ID || 'N/A',
          proveedor: r.proveedor || r.PROVEEDOR || 'Sin Nombre',
          depto: r.DEPARTAMENTO || r.departamento || 'Sin Depto',
          divisa,
          monto: montoNominal,
          montoNormalizadoMXN,
          estatus: r.ESTATUS || r.ESTATUS_COMPACTO || 'Proceso'
        };
      })
      .sort((a, b) => b.montoNormalizadoMXN - a.montoNormalizadoMXN)
      .slice(0, 5);
  }, [uniqueSolicitudes]);

  // 🔹 TOP SOLICITUDES CON MONTOS MÁS PEQUEÑOS (NORMALIZADO A MXN PARA COMPARACIÓN JUSTA)
  const topMontosPequenos = useMemo(() => {
    return uniqueSolicitudes
      .map(r => {
        const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
        const isUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
        const montoNominal = parseVal(r);
        const montoNormalizadoMXN = isUsd ? montoNominal * 20 : montoNominal;

        return {
          folio: r.FOLIO_OC || r.FOLIO || r.orden || r.AUTSOLPAGOID || r.ID || 'N/A',
          proveedor: r.proveedor || r.PROVEEDOR || 'Sin Nombre',
          depto: r.DEPARTAMENTO || r.departamento || 'Sin Depto',
          divisa,
          monto: montoNominal,
          montoNormalizadoMXN,
          estatus: r.ESTATUS || r.ESTATUS_COMPACTO || 'Proceso'
        };
      })
      .filter(r => r.monto > 0)
      .sort((a, b) => a.montoNormalizadoMXN - b.montoNormalizadoMXN)
      .slice(0, 5);
  }, [uniqueSolicitudes]);

  // 🏢 DEPARTAMENTOS QUE MÁS PIDEN (DESDUPLICADOS POR FOLIO)
  const deptosMasPiden = useMemo(() => {
    const map = {};
    uniqueSolicitudes.forEach(r => {
      const depto = String(r.DEPARTAMENTO || r.departamento || 'Sin Depto').trim();
      const val = parseVal(r);
      if (!map[depto]) map[depto] = { depto, count: 0, monto: 0 };
      map[depto].count++;
      map[depto].monto += val;
    });

    return Object.values(map)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [uniqueSolicitudes]);

  // 🤝 PROVEEDORES CON MÁS SOLICITUDES GENERADAS (DESDUPLICADOS POR FOLIO)
  const proveedoresMasSolicitados = useMemo(() => {
    const map = {};
    uniqueSolicitudes.forEach(r => {
      const prov = String(r.proveedor || r.PROVEEDOR || 'Sin Nombre').trim();
      const val = parseVal(r);
      if (!map[prov]) map[prov] = { prov, count: 0, monto: 0 };
      map[prov].count++;
      map[prov].monto += val;
    });

    return Object.values(map)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [uniqueSolicitudes]);

  // 🚦 SEMÁFORO DE CUELLO DE BOTELLA Y AUTORIZADORES RETENEDORES (>3, >5, >7 DÍAS)
  const semaforoEstancadas = useMemo(() => {
    let mas3Dias = 0, mas5Dias = 0, mas7Dias = 0;
    const autorizadoresMap = {};
    const solicitudesDetalle = [];

    uniqueSolicitudes.forEach(r => {
      // 1. Identificar si es una solicitud pendiente por autorizar
      const countSolicitud = Number(r?.SOLICITUDES_COUNT || 0)
        || (String(r?.TIENE_SOLICITUD || '').toUpperCase() === 'SI' ? 1 : 0)
        || (r?.ID_SOLICITUD ? 1 : 0);

      const authFlow = Array.isArray(r?.cadenaAut)
        ? r.cadenaAut.filter(item => String(item?.ROL || '').startsWith('Autorizador '))
        : [];
      const authComplete = authFlow.length > 0 && authFlow.every(item => item?.ESTADO === 'CHECK_GREEN');

      const isPorAutorizar = countSolicitud > 0 && !authComplete;
      if (!isPorAutorizar) return;

      const rawDate = r.FECHASOLICITUD || r.FECHA_SOLICITUD || r.SOLICITUD || r.FECHA_OC || r.FECHA;
      const dias = calcDiasRetraso(rawDate);
      
      // Identificar cuál autorizador está pendiente en la cadenaAut
      const pendItem = authFlow.find(item => item?.ESTADO !== 'CHECK_GREEN');
      const autorizadorPendiente = pendItem?.USUARIO || pendItem?.ROL || 'Por Asignar';

      // Si tiene al menos 1 día de retraso (o cualquier valor >0), evaluamos rangos
      if (dias >= 3) mas3Dias++;
      if (dias >= 5) mas5Dias++;
      if (dias >= 7) mas7Dias++;

      // Agregamos a la tabla de estancamiento (cualquiera con >= 1 día de creación)
      if (dias >= 1 || solicitudesDetalle.length < 10) {
        solicitudesDetalle.push({
          folio: r.FOLIO_OC || r.FOLIO || r.orden || r.AUTSOLPAGOID || r.ID || 'N/A',
          proveedor: r.proveedor || r.PROVEEDOR || 'Sin Nombre',
          empresa: r.EMPRESA || r.NOMBRE_EMPRESA || 'Empresa',
          monto: parseVal(r),
          divisa: String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim(),
          dias,
          autorizadorPendiente
        });

        if (!autorizadoresMap[autorizadorPendiente]) {
          autorizadoresMap[autorizadorPendiente] = { usuario: autorizadorPendiente, count: 0, montoTotal: 0 };
        }
        autorizadoresMap[autorizadorPendiente].count++;
        autorizadoresMap[autorizadorPendiente].montoTotal += parseVal(r);
      }
    });

    const topAutorizadoresEstancadores = Object.values(autorizadoresMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);

    return {
      mas3Dias,
      mas5Dias,
      mas7Dias,
      topAutorizadoresEstancadores,
      solicitudesDetalle: solicitudesDetalle.sort((a, b) => b.dias - a.dias).slice(0, 10)
    };
  }, [uniqueSolicitudes]);

  // 🏦 SEMÁFORO DE TESORERÍA: AUTORIZADAS PENDIENTES DE PAGO CON DÍAS ACUMULADOS
  const semaforoTesoreria = useMemo(() => {
    let alDia = 0, mas3Dias = 0, mas5Dias = 0, mas7Dias = 0;
    const solicitudesDetalle = [];

    filteredPendientes.forEach(r => {
      const rawDate = r.FECHA_SOLICITUD_PAGO || r.FECHASOLICITUD || r.FECHA_SOLICITUD || r.FECHA_OC || r.FECHA;
      const dias = calcDiasRetraso(rawDate);

      if (dias >= 7) mas7Dias++;
      else if (dias >= 5) mas5Dias++;
      else if (dias >= 3) mas3Dias++;
      else alDia++;

      solicitudesDetalle.push({
        folio: r.FOLIO_OC || r.FOLIO || r.orden || r.AUTSOLPAGOID || r.ID || 'N/A',
        proveedor: r.proveedor || r.PROVEEDOR || 'Sin Nombre',
        empresa: r.EMPRESA || r.NOMBRE_EMPRESA || 'Empresa',
        monto: parseVal(r),
        divisa: String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim(),
        dias,
        estatus: 'Autorizada (Pend. Pago Tesorería)'
      });
    });

    return {
      alDia,
      mas3Dias,
      mas5Dias,
      mas7Dias,
      totalPendientes: filteredPendientes.length,
      solicitudesDetalle: solicitudesDetalle.sort((a, b) => b.dias - a.dias).slice(0, 10)
    };
  }, [filteredPendientes]);

  // 🍩 DISTRIBUCIÓN POR MONEDA (PESOS MXN VS DÓLARES USD)
  const distribucionMonedaData = useMemo(() => {
    let mxnMonto = 0, usdMonto = 0;
    uniqueSolicitudes.forEach(r => {
      const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
      const val = parseVal(r);
      if (divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR') {
        usdMonto += val;
      } else {
        mxnMonto += val;
      }
    });
    return [
      { name: 'Pesos MXN 🇲🇽', value: mxnMonto, color: '#0f766e' },
      { name: 'Dólares USD 🇺🇸', value: usdMonto, color: '#0284c7' }
    ].filter(d => d.value > 0);
  }, [uniqueSolicitudes]);

  // 📦 DISTRIBUCIÓN POR CÓDIGO Y DESCRIPCIÓN (PARTIDAS INDIVIDUALES / PRODUCTOS / LITROS)
  const distribucionTipoData = useMemo(() => {
    const map = {};

    uniqueSolicitudes.forEach(r => {
      // 1. Si la solicitud cuenta con un arreglo de partidas (detalles de productos/insumos)
      const partidas = Array.isArray(r.partidas) && r.partidas.length > 0 ? r.partidas : null;

      if (partidas) {
        partidas.forEach(p => {
          const codigo = String(p.CODIGO_PART || p.CODIGO || p.CLAVE || 'ART').trim().toUpperCase();
          const descripcion = String(p.DESCRIPCION_PART || p.DESCRIPCION || p.CONCEPTO || 'Concepto General').trim();
          const key = `${codigo}_${descripcion}`;
          const val = Number(p.IMPORTE || p.MONTO || p.TOTAL || 0) || (parseVal(r) / partidas.length);
          const cantidadPiezas = Number(p.CANTIDAD || p.CANT || p.UNIDADES || 1) || 1;

          if (!map[key]) {
            map[key] = { key, codigo, descripcion, countSol: 0, totalPiezas: 0, monto: 0 };
          }
          map[key].countSol++;
          map[key].totalPiezas += cantidadPiezas;
          map[key].monto += val;
        });
      } else {
        // 2. Si es una solicitud global sin arreglo de partidas desglosadas
        const descRaw = String(r.DESCRIPCION || r.CONCEPTO || r.TIPO_SOLICITUD || 'Gastos Generales').trim();
        // Intentar buscar código al inicio del string (ej: "SER209 - GASOLINA")
        const matchCode = descRaw.match(/^([A-Z0-9_-]{3,15})\s*[-:]?\s*(.*)$/i);
        let codigo = matchCode && matchCode[1] ? matchCode[1].toUpperCase() : 'GEN';
        let descripcion = matchCode && matchCode[2] ? matchCode[2].trim() : descRaw;

        if (codigo === 'GEN' && descRaw.includes('-')) {
          const parts = descRaw.split('-');
          codigo = parts[0].trim().toUpperCase();
          descripcion = parts.slice(1).join('-').trim();
        }

        const key = `${codigo}_${descripcion}`;
        const val = parseVal(r);

        if (!map[key]) {
          map[key] = { key, codigo, descripcion, countSol: 0, totalPiezas: 0, monto: 0 };
        }
        map[key].countSol++;
        map[key].totalPiezas += 1;
        map[key].monto += val;
      }
    });

    return Object.values(map)
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 10);
  }, [uniqueSolicitudes]);

  // 🏛️ MONTO DE GASTOS ACUMULADO POR DEPARTAMENTO
  const gastosPorDeptoData = useMemo(() => {
    const map = {};
    uniqueSolicitudes.forEach(r => {
      const depto = String(r.DEPARTAMENTO || r.departamento || 'General').trim();
      const val = parseVal(r);
      if (!map[depto]) map[depto] = { depto, monto: 0, count: 0 };
      map[depto].monto += val;
      map[depto].count++;
    });

    return Object.values(map)
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 7);
  }, [uniqueSolicitudes]);

  // 💵 METRICAS SEGUNDO DASHBOARD (FINANCIERO)
  const metricsFinancieras = useMemo(() => {
    let pendMxn = 0, pendUsd = 0;
    let atrasoSum = 0, atrasoCount = 0;

    filteredPendientes.forEach(r => {
      const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
      const val = parseVal(r);
      if (divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR') {
        pendUsd += val;
      } else {
        pendMxn += val;
      }

      const rawDate = r.FECHA_SOLICITUD_PAGO || r.FECHA_REPORTE || r.FECHASOLICITUD || r.FECHA;
      const dias = calcDiasRetraso(rawDate);
      if (dias > 0) {
        atrasoSum += dias;
        atrasoCount++;
      }
    });

    let porAutMxn = 0, porAutUsd = 0;
    filteredPorAutorizar.forEach(r => {
      const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
      const val = parseVal(r);
      if (divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR') {
        porAutUsd += val;
      } else {
        porAutMxn += val;
      }
    });

    const promAtraso = atrasoCount > 0 ? Math.round(atrasoSum / atrasoCount) : 0;

    return { pendMxn, pendUsd, porAutMxn, porAutUsd, promAtraso };
  }, [filteredPendientes, filteredPorAutorizar]);

  // 📈 TENDENCIA MENSUAL
  const tendenciaMensualData = useMemo(() => {
    const mesesMap = Array.from({ length: 12 }, (_, i) => ({
      mesIndex: i,
      mesLabel: MESES[i],
      porAutorizar: 0,
      pendientePagar: 0,
    }));

    filteredPorAutorizar.forEach(r => {
      const rawDate = r.FECHASOLICITUD || r.FECHA_SOLICITUD || r.FECHA_OC || r.FECHA;
      if (!rawDate) return;
      const strDate = String(rawDate).trim();
      let rowDateStr = strDate.split('T')[0].split(' ')[0];
      const mxMatch = strDate.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (mxMatch) rowDateStr = `${mxMatch[3]}-${mxMatch[2]}-${mxMatch[1]}`;
      const f = new Date(rowDateStr);
      if (!isNaN(f.getTime())) {
        const m = f.getMonth();
        mesesMap[m].porAutorizar += parseVal(r);
      }
    });

    filteredPendientes.forEach(r => {
      const rawDate = r.FECHASOLICITUD || r.FECHA_SOLICITUD || r.FECHA_OC || r.FECHA;
      if (!rawDate) return;
      const strDate = String(rawDate).trim();
      let rowDateStr = strDate.split('T')[0].split(' ')[0];
      const mxMatch = strDate.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (mxMatch) rowDateStr = `${mxMatch[3]}-${mxMatch[2]}-${mxMatch[1]}`;
      const f = new Date(rowDateStr);
      if (!isNaN(f.getTime())) {
        const m = f.getMonth();
        mesesMap[m].pendientePagar += parseVal(r);
      }
    });

    return mesesMap;
  }, [filteredPorAutorizar, filteredPendientes]);

  // Antigüedad de Saldos por Rangos Financieros (Semáforo Directivo)
  const antiguedadTablaData = useMemo(() => {
    const rangos = [
      { key: 'r0_15', label: '🟢 0 a 15 Días (Normal)', min: 0, max: 15, count: 0, mxn: 0, usd: 0, color: '#16a34a', bg: '#f0fdf4' },
      { key: 'r16_30', label: '🟡 16 a 30 Días (Atención)', min: 16, max: 30, count: 0, mxn: 0, usd: 0, color: '#0284c7', bg: '#f0f9ff' },
      { key: 'r31_60', label: '🟠 31 a 60 Días (Urgente)', min: 31, max: 60, count: 0, mxn: 0, usd: 0, color: '#d97706', bg: '#fffbeb' },
      { key: 'r60_plus', label: '🔴 +60 Días (Crítico / Alerta Corte)', min: 61, max: 99999, count: 0, mxn: 0, usd: 0, color: '#dc2626', bg: '#fef2f2' },
    ];

    filteredPendientes.forEach(r => {
      const rawDate = r.FECHA_SOLICITUD_PAGO || r.FECHA_REPORTE || r.FECHASOLICITUD || r.FECHA;
      const dias = calcDiasRetraso(rawDate);
      const divisa = String(r.moneda || r.DIVISA || r.MONEDA || 'MXN').toUpperCase().trim();
      const isUsd = divisa === 'USD' || divisa === 'DOLARES' || divisa === 'DOLAR';
      const val = parseVal(r);

      const target = rangos.find(rg => dias >= rg.min && dias <= rg.max) || rangos[3];
      target.count++;
      if (isUsd) target.usd += val;
      else target.mxn += val;
    });

    return rangos;
  }, [filteredPendientes]);

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 'clamp(8px, 1.2vw, 16px)', bgcolor: '#f8fafc' }}>
      
      {/* ── BARRA DE SELECCIÓN DE DASHBOARD Y FILTROS ─────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 'clamp(8px, 1.2vw, 14px)', mb: 'clamp(8px, 1.2vw, 14px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
        
        {/* NAVEGACIÓN ENTRE DASHBOARDS */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 0.8, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 1 }}>
          <Tabs
            value={activeDashboard}
            onChange={(e, v) => setActiveDashboard(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 'clamp(30px, 2.6vw, 36px)',
              '& .MuiTab-root': {
                fontWeight: 900,
                fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)',
                textTransform: 'none',
                px: 'clamp(10px, 1.2vw, 16px)',
                minHeight: 'clamp(30px, 2.6vw, 36px)',
                py: 0.5
              }
            }}
          >
            <Tab label="📑 Dashboard 1: Análisis de Solicitudes" />
            <Tab label="💵 Dashboard 2: Control Mando Financiero (Liquidez)" />
          </Tabs>

          <Button
            variant="contained"
            size="small"
            endIcon={activeDashboard === 0 ? <ArrowForward sx={{ fontSize: 'clamp(12px, 1vw, 16px)' }} /> : <ArrowBack sx={{ fontSize: 'clamp(12px, 1vw, 16px)' }} />}
            onClick={() => setActiveDashboard(prev => (prev === 0 ? 1 : 0))}
            sx={{
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: '7px',
              bgcolor: '#0284c7',
              fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
              height: 'clamp(26px, 2.2vw, 32px)',
              px: 'clamp(8px, 1vw, 14px)'
            }}
          >
            {activeDashboard === 0 ? 'Ver Dashboard Financiero ➔' : '⬅ Ver Dashboard Solicitudes'}
          </Button>
        </Box>

        {/* CONTROLES DE FILTRO */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center', flexGrow: 1 }}>
            
            <FormControl size="small" sx={{ width: 'clamp(130px, 12vw, 160px)' }}>
              <InputLabel id="emp-label" sx={{ fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.76rem)', fontWeight: 600 }}>Empresa</InputLabel>
              <Select
                labelId="emp-label"
                value={filtroEmpresa}
                label="Empresa"
                onChange={(e) => setFiltroEmpresa(e.target.value)}
                sx={{ height: 'clamp(28px, 2.4vw, 34px)', bgcolor: '#f8fafc', borderRadius: '7px', fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)', fontWeight: 700 }}
                MenuProps={{ disableRestoreFocus: true }}
              >
                <MenuItem value=""><em>Todas las Empresas</em></MenuItem>
                {optEmpresas.map((emp, i) => (
                  <MenuItem key={i} value={emp}>{emp}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              size="small"
              options={optCentrosCosto}
              value={filtroCc}
              onChange={(e, val) => setFiltroCc(val || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Centro Costo"
                  sx={{
                    width: 'clamp(120px, 11vw, 150px)',
                    '& .MuiOutlinedInput-root': { height: 'clamp(28px, 2.4vw, 34px)', fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)', bgcolor: '#f8fafc', borderRadius: '7px' }
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              options={optDepartamentos}
              value={filtroDepto}
              onChange={(e, val) => setFiltroDepto(val || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Departamento"
                  sx={{
                    width: 'clamp(120px, 11vw, 150px)',
                    '& .MuiOutlinedInput-root': { height: 'clamp(28px, 2.4vw, 34px)', fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)', bgcolor: '#f8fafc', borderRadius: '7px' }
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              options={optProveedores}
              value={filtroProveedor}
              onChange={(e, val) => setFiltroProveedor(val || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Cliente / Proveedor"
                  sx={{
                    width: 'clamp(140px, 14vw, 180px)',
                    '& .MuiOutlinedInput-root': { height: 'clamp(28px, 2.4vw, 34px)', fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)', bgcolor: '#f8fafc', borderRadius: '7px' }
                  }}
                />
              )}
            />

            <TextField
              size="small"
              type="date"
              label="Desde"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true, sx: { fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.76rem)', fontWeight: 600 } }}
              sx={{
                width: 'clamp(115px, 10vw, 130px)',
                '& .MuiOutlinedInput-root': { height: 'clamp(28px, 2.4vw, 34px)', fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', bgcolor: '#f8fafc', borderRadius: '7px' }
              }}
            />
            <TextField
              size="small"
              type="date"
              label="Hasta"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true, sx: { fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.76rem)', fontWeight: 600 } }}
              sx={{
                width: 'clamp(115px, 10vw, 130px)',
                '& .MuiOutlinedInput-root': { height: 'clamp(28px, 2.4vw, 34px)', fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', bgcolor: '#f8fafc', borderRadius: '7px' }
              }}
            />

            {(filtroEmpresa || filtroCc || filtroDepto || filtroProveedor || fechaInicio || fechaFin) && (
              <Button
                size="small"
                color="error"
                onClick={() => { setFiltroEmpresa(''); setFiltroCc(''); setFiltroDepto(''); setFiltroProveedor(''); setFechaInicio(''); setFechaFin(''); }}
                sx={{ textTransform: 'none', fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)' }}
              >
                Limpiar
              </Button>
            )}
          </Box>

          <Button
            size="small"
            onClick={fetchData}
            startIcon={<Refresh sx={{ fontSize: 'clamp(13px, 1vw, 17px)' }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              height: 'clamp(28px, 2.4vw, 34px)',
              fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
              borderRadius: '7px',
              border: '1px solid #e2e8f0'
            }}
          >
            Actualizar
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} sx={{ color: '#0655A5' }} />
        </Box>
      ) : activeDashboard === 0 ? (
        
        /* ========================================================================= */
        /* 📑 DASHBOARD 1: ANÁLISIS DE SOLICITUDES Y OPERACIÓN DE COMPRAS           */
        /* ========================================================================= */
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            📋 CONTEO Y ESTADO GENERAL DE LAS SOLICITUDES EN SISTEMA
          </Typography>

          {/* 5 CARDS DE MÉTRICAS DE SOLICITUDES AUTO-RESPONSIVAS */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(170px, 17vw, 240px)), 1fr))',
              gap: 'clamp(8px, 1vw, 14px)',
              mb: 'clamp(12px, 1.5vw, 20px)'
            }}
          >
            
            {/* 1. Solicitudes Generadas */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.2vw, 14px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>TOTAL GENERADAS</Typography>
              <Typography sx={{ fontSize: 'clamp(1.1rem, 1.05rem + 0.3vw, 1.45rem)', fontWeight: 900, color: '#0f172a', mt: 0.2, lineHeight: 1.1 }}>
                {solicitudesMetrics.totalGeneradas}
              </Typography>
              <Box sx={{ mt: 0.6, pt: 0.6, borderTop: '1px dashed #e2e8f0' }}>
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#334155' }}>
                  🇲🇽 {formatMoney(solicitudesMetrics.totalMxn, 'MXN')}
                </Typography>
                {solicitudesMetrics.totalUsd > 0 && (
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#0284c7', mt: 0.2 }}>
                    🇺🇸 {formatMoney(solicitudesMetrics.totalUsd, 'USD')}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* 2. Con Solicitud de Pago */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.2vw, 14px)', borderRadius: '10px', border: '1px solid #bae6fd', bgcolor: '#f0f9ff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase' }}>CON SOLICITUD PAGO</Typography>
              <Typography sx={{ fontSize: 'clamp(1.1rem, 1.05rem + 0.3vw, 1.45rem)', fontWeight: 900, color: '#0284c7', mt: 0.2, lineHeight: 1.1 }}>
                {solicitudesMetrics.conSolicitudPago}
              </Typography>
              <Box sx={{ mt: 0.6, pt: 0.6, borderTop: '1px dashed #cbd5e1' }}>
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#0f172a' }}>
                  🇲🇽 {formatMoney(solicitudesMetrics.conSolMxn, 'MXN')}
                </Typography>
                {solicitudesMetrics.conSolUsd > 0 && (
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#0369a1', mt: 0.2 }}>
                    🇺🇸 {formatMoney(solicitudesMetrics.conSolUsd, 'USD')}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* 3. Pendientes por Autorizar */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.2vw, 14px)', borderRadius: '10px', border: '1px solid #fde68a', bgcolor: '#fffbeb', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#b45309', textTransform: 'uppercase' }}>POR AUTORIZAR</Typography>
              <Typography sx={{ fontSize: 'clamp(1.1rem, 1.05rem + 0.3vw, 1.45rem)', fontWeight: 900, color: '#d97706', mt: 0.2, lineHeight: 1.1 }}>
                {solicitudesMetrics.porAutorizarCount}
              </Typography>
              <Box sx={{ mt: 0.6, pt: 0.6, borderTop: '1px dashed #fcd34d' }}>
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#78350f' }}>
                  🇲🇽 {formatMoney(solicitudesMetrics.porAutMxn, 'MXN')}
                </Typography>
                {solicitudesMetrics.porAutUsd > 0 && (
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#b45309', mt: 0.2 }}>
                    🇺🇸 {formatMoney(solicitudesMetrics.porAutUsd, 'USD')}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* 4. Pendientes de Pago */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.2vw, 14px)', borderRadius: '10px', border: '1px solid #fed7aa', bgcolor: '#fff7ed', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#c2410c', textTransform: 'uppercase' }}>PENDIENTES DE PAGO</Typography>
              <Typography sx={{ fontSize: 'clamp(1.1rem, 1.05rem + 0.3vw, 1.45rem)', fontWeight: 900, color: '#ea580c', mt: 0.2, lineHeight: 1.1 }}>
                {solicitudesMetrics.pendientesPagoCount}
              </Typography>
              <Box sx={{ mt: 0.6, pt: 0.6, borderTop: '1px dashed #fdba74' }}>
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#7c2d12' }}>
                  🇲🇽 {formatMoney(solicitudesMetrics.pendPagoMxn, 'MXN')}
                </Typography>
                {solicitudesMetrics.pendPagoUsd > 0 && (
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#ea580c', mt: 0.2 }}>
                    🇺🇸 {formatMoney(solicitudesMetrics.pendPagoUsd, 'USD')}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* 5. Ya Pagadas */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.2vw, 14px)', borderRadius: '10px', border: '1px solid #bbf7d0', bgcolor: '#f0fdf4', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#15803d', textTransform: 'uppercase' }}>PAGADAS / HISTORIAL</Typography>
              <Typography sx={{ fontSize: 'clamp(1.1rem, 1.05rem + 0.3vw, 1.45rem)', fontWeight: 900, color: '#16a34a', mt: 0.2, lineHeight: 1.1 }}>
                {solicitudesMetrics.pagadasCount}
              </Typography>
              <Box sx={{ mt: 0.6, pt: 0.6, borderTop: '1px dashed #86efac' }}>
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#14532d' }}>
                  🇲🇽 {formatMoney(solicitudesMetrics.pagadasMxn, 'MXN')}
                </Typography>
                {solicitudesMetrics.pagadasUsd > 0 && (
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#16a34a', mt: 0.2 }}>
                    🇺🇸 {formatMoney(solicitudesMetrics.pagadasUsd, 'USD')}
                  </Typography>
                )}
              </Box>
            </Paper>

          </Box>

          {/* TABLAS COMPARATIVAS DE MONTOS ALTOS VS PEQUEÑOS */}
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            📊 TOP DE SOLICITUDES: MONTOS ALTOS VS. MONTOS MENORES
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(300px, 32vw, 550px)), 1fr))',
              gap: 'clamp(10px, 1.2vw, 16px)',
              mb: 'clamp(14px, 1.8vw, 24px)'
            }}
          >
            
            {/* Tabla Top Montos Altos */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a' }}>
                  ⬆️ Top 5 Solicitudes con Montos Más Altos
                </Typography>
                <VerticalAlignTop sx={{ color: '#0284c7', fontSize: 'clamp(16px, 1.3vw, 20px)' }} />
              </Box>

              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(6px, 0.8vw, 10px)', borderBottom: '1px solid #f1f5f9' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>FOLIO</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>PROVEEDOR</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>MONTO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topMontosAltos.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', color: '#0284c7' }}>{row.folio}</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0f172a' }}>{row.proveedor.length > 25 ? `${row.proveedor.substring(0, 25)}...` : row.proveedor}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', color: '#0f172a' }}>{formatMoney(row.monto, row.divisa)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            {/* Tabla Top Montos Pequeños */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a' }}>
                  ⬇️ Top 5 Solicitudes con Montos Pequeños
                </Typography>
                <VerticalAlignBottom sx={{ color: '#16a34a', fontSize: 'clamp(16px, 1.3vw, 20px)' }} />
              </Box>

              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(6px, 0.8vw, 10px)', borderBottom: '1px solid #f1f5f9' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>FOLIO</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>PROVEEDOR</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>MONTO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topMontosPequenos.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', color: '#16a34a' }}>{row.folio}</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0f172a' }}>{row.proveedor.length > 25 ? `${row.proveedor.substring(0, 25)}...` : row.proveedor}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', color: '#0f172a' }}>{formatMoney(row.monto, row.divisa)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

          </Box>

          {/* DEPARTAMENTOS Y PROVEEDORES QUE MÁS PIDEN */}
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            🏢 ÁREAS Y PROVEEDORES CON MAYOR VOLUMEN DE SOLICITUDES
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(320px, 34vw, 580px)), 1fr))',
              gap: 'clamp(10px, 1.2vw, 16px)',
              mb: 'clamp(14px, 1.8vw, 24px)'
            }}
          >
            
            {/* Departamentos Unificado: Gráfica de Barras + Tabla de Detalle */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a', mb: 0.3 }}>
                🏬 Análisis Integrado por Departamento
              </Typography>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#64748b', mb: 1 }}>
                Volumen monetario en gráfica (izquierda) y conteo detallado de solicitudes (derecha)
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
                {/* Gráfica de Barras a la Izquierda */}
                <Box>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={gastosPorDeptoData} layout="vertical" margin={{ top: 5, right: 10, left: 30, bottom: 5 }}>
                      <XAxis type="number" tickFormatter={formatCompactMoney} tick={{ fontSize: 9 }} />
                      <YAxis dataKey="depto" type="category" tick={{ fontSize: 8.5, fontWeight: 700 }} width={75} />
                      <Tooltip formatter={(val) => formatMoney(val)} />
                      <Bar dataKey="monto" name="Monto Solicitado" fill="#0f766e" radius={[0, 5, 5, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Tabla de Conteos a la Derecha */}
                <Box sx={{ borderLeft: { sm: '1px dashed #cbd5e1' }, pl: { sm: 1 } }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(3px, 0.4vw, 5px)', px: 'clamp(4px, 0.6vw, 8px)', borderBottom: '1px solid #f1f5f9' } }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>DEPTO</TableCell>
                        <TableCell align="center" sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>CANT.</TableCell>
                        <TableCell align="right" sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>MONTO</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deptosMasPiden.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#0f172a' }}>{row.depto.length > 14 ? `${row.depto.substring(0, 14)}...` : row.depto}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 900, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#0284c7' }}>{row.count}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#0f172a' }}>{formatMoney(row.monto)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Paper>

            {/* Proveedores más solicitados */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a', mb: 1 }}>
                🤝 Clientes / Proveedores con Mayor Frecuencia de Solicitud
              </Typography>
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(6px, 0.8vw, 10px)', borderBottom: '1px solid #f1f5f9' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>PROVEEDOR</TableCell>
                    <TableCell align="center" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>CANTIDAD</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>MONTO ACUMULADO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proveedoresMasSolicitados.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0f172a' }}>{row.prov.length > 25 ? `${row.prov.substring(0, 25)}...` : row.prov}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0284c7' }}>{row.count} sol.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', color: '#0f172a' }}>{formatMoney(row.monto)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

          </Box>

          {/* 🚦 SEMÁFORO DE CUELLO DE BOTELLA Y SOLICITUDES ESTANCADAS */}
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            🚦 CUELLOS DE BOTELLA: SOLICITUDES ESTANCADAS EN AUTORIZACIÓN
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(280px, 30vw, 420px)), 1fr))',
              gap: 'clamp(10px, 1.2vw, 16px)',
              mb: 'clamp(14px, 1.8vw, 24px)'
            }}
          >
            
            {/* Tarjeta de Resumen de Semáforo */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #fde68a', bgcolor: '#fffbeb', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <Warning sx={{ color: '#d97706', fontSize: 'clamp(18px, 1.5vw, 22px)' }} />
                <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#92400e' }}>
                  Alerta de Firmas Pendientes
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#78350f', mb: 1.5, fontWeight: 600 }}>
                Solicitudes sin autorizar con días acumulados en espera de firma directiva:
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.8, mb: 1.5, textAlign: 'center' }}>
                <Paper elevation={0} sx={{ p: 0.6, border: '1px solid #fef08a', bgcolor: '#ffffff', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.95rem, 0.90rem + 0.2vw, 1.2rem)', fontWeight: 900, color: '#d97706', lineHeight: 1.1 }}>{semaforoEstancadas.mas3Dias}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 800, color: '#b45309' }}>≥ 3 Días</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 0.6, border: '1px solid #fed7aa', bgcolor: '#ffffff', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.95rem, 0.90rem + 0.2vw, 1.2rem)', fontWeight: 900, color: '#ea580c', lineHeight: 1.1 }}>{semaforoEstancadas.mas5Dias}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 800, color: '#c2410c' }}>≥ 5 Días</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 0.6, border: '1px solid #fecaca', bgcolor: '#ffffff', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.95rem, 0.90rem + 0.2vw, 1.2rem)', fontWeight: 900, color: '#dc2626', lineHeight: 1.1 }}>{semaforoEstancadas.mas7Dias}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 800, color: '#b91c1c' }}>≥ 7 Días</Typography>
                </Paper>
              </Box>

              <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 900, color: '#78350f', mb: 0.8 }}>
                👤 Autorizadores Retenedores:
              </Typography>
              {semaforoEstancadas.topAutorizadoresEstancadores.length === 0 ? (
                <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#15803d', fontWeight: 700 }}>✓ Al día: Sin solicitudes con demora en firma.</Typography>
              ) : (
                semaforoEstancadas.topAutorizadoresEstancadores.map((aut, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3, borderBottom: '1px dashed #fde68a' }}>
                    <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', fontWeight: 800, color: '#451a03' }}>
                      {aut.usuario}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#dc2626', bgcolor: '#fee2e2', px: 0.6, py: 0.2, borderRadius: '4px' }}>
                        {aut.count} retenidas
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Paper>

            {/* Tabla de Detalle de Solicitudes Estancadas */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a', mb: 1 }}>
                📋 Solicitudes con Mayor Retraso en Autorización
              </Typography>

              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(5px, 0.7vw, 8px)', borderBottom: '1px solid #f1f5f9' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>FOLIO / EMPRESA</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>PROVEEDOR</TableCell>
                    <TableCell align="center" sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>DEMORA</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>AUTORIZADOR</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>MONTO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {semaforoEstancadas.solicitudesDetalle.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, color: '#64748b', fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', fontWeight: 700 }}>
                        🟢 Excelente: Las solicitudes pendientes han sido creadas recientemente (menos de 3 días).
                      </TableCell>
                    </TableRow>
                  ) : (
                    semaforoEstancadas.solicitudesDetalle.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0284c7' }}>
                          {row.folio}
                          <Typography sx={{ fontSize: 'clamp(0.50rem, 0.48rem + 0.04vw, 0.58rem)', color: '#64748b', fontWeight: 600 }}>{row.empresa}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#0f172a' }}>
                          {row.proveedor.length > 18 ? `${row.proveedor.substring(0, 18)}...` : row.proveedor}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{
                            display: 'inline-block',
                            px: 0.6, py: 0.2, borderRadius: '4px',
                            fontWeight: 900, fontSize: 'clamp(0.54rem, 0.52rem + 0.05vw, 0.62rem)',
                            bgcolor: row.dias >= 7 ? '#fee2e2' : row.dias >= 5 ? '#ffedd5' : '#fef9c3',
                            color: row.dias >= 7 ? '#991b1b' : row.dias >= 5 ? '#9a3412' : '#854d0e'
                          }}>
                            {row.dias}d
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.66rem)', color: '#b45309' }}>
                          {row.autorizadorPendiente}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0f172a' }}>
                          {formatMoney(row.monto, row.divisa)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>

          </Box>

          {/* 🏦 SEMÁFORO DE CUELLO DE BOTELLA EN TESORERÍA (AUTORIZADAS PENDIENTES DE PAGO) */}
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            🏦 CUELLOS DE BOTELLA EN TESORERÍA: SOLICITUDES AUTORIZADAS PENDIENTES DE PAGO
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(280px, 30vw, 420px)), 1fr))',
              gap: 'clamp(10px, 1.2vw, 16px)',
              mb: 'clamp(14px, 1.8vw, 24px)'
            }}
          >
            
            {/* Tarjeta de Resumen de Semáforo de Tesorería */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #fed7aa', bgcolor: '#fff7ed', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <AccountBalance sx={{ color: '#ea580c', fontSize: 'clamp(18px, 1.5vw, 22px)' }} />
                <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#9a3412' }}>
                  Atraso de Pago en Tesorería
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#7c2d12', mb: 1.5, fontWeight: 600 }}>
                Solicitudes 100% autorizadas que esperan dispersión/pago en bancos:
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.6, mb: 1.5, textAlign: 'center' }}>
                <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #bbf7d0', bgcolor: '#f0fdf4', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.85rem, 0.80rem + 0.18vw, 1.05rem)', fontWeight: 900, color: '#16a34a', lineHeight: 1.1 }}>{semaforoTesoreria.alDia}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.52rem, 0.50rem + 0.04vw, 0.60rem)', fontWeight: 800, color: '#15803d' }}>0-2 Días</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #fef08a', bgcolor: '#ffffff', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.85rem, 0.80rem + 0.18vw, 1.05rem)', fontWeight: 900, color: '#d97706', lineHeight: 1.1 }}>{semaforoTesoreria.mas3Dias}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.52rem, 0.50rem + 0.04vw, 0.60rem)', fontWeight: 800, color: '#b45309' }}>3-4 Días</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #fed7aa', bgcolor: '#ffffff', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.85rem, 0.80rem + 0.18vw, 1.05rem)', fontWeight: 900, color: '#ea580c', lineHeight: 1.1 }}>{semaforoTesoreria.mas5Dias}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.52rem, 0.50rem + 0.04vw, 0.60rem)', fontWeight: 800, color: '#c2410c' }}>5-6 Días</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 0.5, border: '1px solid #fecaca', bgcolor: '#ffffff', borderRadius: '7px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.85rem, 0.80rem + 0.18vw, 1.05rem)', fontWeight: 900, color: '#dc2626', lineHeight: 1.1 }}>{semaforoTesoreria.mas7Dias}</Typography>
                  <Typography sx={{ fontSize: 'clamp(0.52rem, 0.50rem + 0.04vw, 0.60rem)', fontWeight: 800, color: '#b91c1c' }}>≥ 7 Días</Typography>
                </Paper>
              </Box>

              <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 900, color: '#7c2d12', mb: 0.4 }}>
                📊 Estado General:
              </Typography>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#9a3412' }}>
                {semaforoTesoreria.totalPendientes} solicitudes autorizadas esperando pago.
              </Typography>
            </Paper>

            {/* Tabla de Detalle de Solicitudes Pendientes de Pago en Tesorería */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a', mb: 1 }}>
                📋 Solicitudes Autorizadas con Mayor Atraso de Pago
              </Typography>

              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(5px, 0.7vw, 8px)', borderBottom: '1px solid #f1f5f9' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>FOLIO / EMPRESA</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>PROVEEDOR</TableCell>
                    <TableCell align="center" sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>DEMORA</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>ESTATUS</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 900, color: '#475569' }}>MONTO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {semaforoTesoreria.solicitudesDetalle.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, color: '#64748b', fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', fontWeight: 700 }}>
                        🟢 Al día: No hay solicitudes autorizadas pendientes por pagar en Tesorería.
                      </TableCell>
                    </TableRow>
                  ) : (
                    semaforoTesoreria.solicitudesDetalle.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#ea580c' }}>
                          {row.folio}
                          <Typography sx={{ fontSize: 'clamp(0.50rem, 0.48rem + 0.04vw, 0.58rem)', color: '#64748b', fontWeight: 600 }}>{row.empresa}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#0f172a' }}>
                          {row.proveedor.length > 20 ? `${row.proveedor.substring(0, 20)}...` : row.proveedor}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{
                            display: 'inline-block',
                            px: 0.6, py: 0.2, borderRadius: '4px',
                            fontWeight: 900, fontSize: 'clamp(0.54rem, 0.52rem + 0.05vw, 0.62rem)',
                            bgcolor: row.dias >= 7 ? '#fee2e2' : row.dias >= 5 ? '#ffedd5' : '#fff7ed',
                            color: row.dias >= 7 ? '#991b1b' : row.dias >= 5 ? '#9a3412' : '#c2410c'
                          }}>
                            {row.dias}d
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.66rem)', color: '#16a34a' }}>
                          {row.estatus}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0f172a' }}>
                          {formatMoney(row.monto, row.divisa)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>

          </Box>

          {/* 📊 GRÁFICAS DE DISTRIBUCIÓN Y ANÁLISIS DE GASTOS */}
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            📊 DISTRIBUCIÓN DE GASTOS POR DIVISA Y CANAL
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(280px, 30vw, 420px)), 1fr))',
              gap: 'clamp(10px, 1.2vw, 16px)',
              mb: 'clamp(14px, 1.8vw, 24px)'
            }}
          >
            
            {/* Gráfica 1: Donut de Proporción por Moneda */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a', mb: 0.3 }}>
                🪙 Proporción por Divisa
              </Typography>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#64748b', mb: 1 }}>
                Monto acumulado MXN vs USD
              </Typography>

              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={distribucionMonedaData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={68}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {distribucionMonedaData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => formatMoney(val)} />
                  <Legend verticalAlign="bottom" height={30} wrapperStyle={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', fontWeight: 800 }} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Tarjeta Integrada: Gastos por Código y Descripción (Conceptos) */}
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a', mb: 0.3 }}>
                📦 Gastos por Código y Concepto
              </Typography>
              <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#64748b', mb: 1 }}>
                Desglose inteligente por código de insumo, cantidad de solicitudes y total acumulado
              </Typography>

              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(3px, 0.4vw, 5px)', px: 'clamp(4px, 0.6vw, 8px)', borderBottom: '1px solid #f1f5f9' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 900, color: '#475569', width: 16 }}>#</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 900, color: '#475569' }}>CÓDIGO</TableCell>
                    <TableCell sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 900, color: '#475569' }}>DESCRIPCIÓN</TableCell>
                    <TableCell align="center" sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 900, color: '#475569' }}>SOL.</TableCell>
                    <TableCell align="center" sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 900, color: '#475569' }}>UNID.</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.62rem)', fontWeight: 900, color: '#475569' }}>TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {distribucionTipoData.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.55rem, 0.52rem + 0.05vw, 0.64rem)', color: '#64748b' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 900, fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', color: '#0284c7' }}>
                        {row.codigo}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)', color: '#0f172a' }}>
                        {row.descripcion.length > 25 ? `${row.descripcion.substring(0, 25)}...` : row.descripcion}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900, fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)', color: '#0f766e' }}>
                        {row.countSol}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900, fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)', color: '#d97706' }}>
                        {row.totalPiezas}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#0f172a' }}>
                        {formatMoney(row.monto)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

          </Box>
        </>

      ) : (

        /* ========================================================================= */
        /* 💵 DASHBOARD 2: CONTROL MANDO FINANCIERO (LIQUIDEZ Y RIESGO DE PAGO)       */
        /* ========================================================================= */
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: 0.3, fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)' }}>
            💵 DASHBOARD FINANCIERO: POSICIÓN DE LIQUIDEZ Y CUADRO DE ATRASO
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(200px, 20vw, 280px)), 1fr))',
              gap: 'clamp(8px, 1vw, 14px)',
              mb: 'clamp(14px, 1.8vw, 24px)'
            }}
          >
            
            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 16px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>PENDIENTE POR PAGAR (MN)</Typography>
                  <Typography sx={{ fontSize: 'clamp(1.05rem, 1.0rem + 0.25vw, 1.35rem)', fontWeight: 900, color: '#0f172a', mt: 0.3, lineHeight: 1.1 }}>
                    {formatMoney(metricsFinancieras.pendMxn, 'MXN')}
                  </Typography>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', color: '#16a34a', fontWeight: 800, mt: 0.4 }}>
                    ✓ Listo para Desembolso
                  </Typography>
                </Box>
                <HourglassEmpty sx={{ color: '#0284c7', fontSize: 'clamp(24px, 2vw, 30px)' }} />
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 16px)', borderRadius: '10px', border: '1px solid #bae6fd', bgcolor: '#f0f9ff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase' }}>PENDIENTE POR PAGAR (USD)</Typography>
                  <Typography sx={{ fontSize: 'clamp(1.05rem, 1.0rem + 0.25vw, 1.35rem)', fontWeight: 900, color: '#0284c7', mt: 0.3, lineHeight: 1.1 }}>
                    {formatMoney(metricsFinancieras.pendUsd, 'USD')}
                  </Typography>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', color: '#0369a1', fontWeight: 800, mt: 0.4 }}>
                    💵 Compromiso en Dólares
                  </Typography>
                </Box>
                <AttachMoney sx={{ color: '#0284c7', fontSize: 'clamp(24px, 2vw, 30px)' }} />
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 16px)', borderRadius: '10px', border: '1px solid #fde68a', bgcolor: '#fffbeb', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#b45309', textTransform: 'uppercase' }}>ATORADO EN FIRMAS (MN)</Typography>
                  <Typography sx={{ fontSize: 'clamp(1.05rem, 1.0rem + 0.25vw, 1.35rem)', fontWeight: 900, color: '#d97706', mt: 0.3, lineHeight: 1.1 }}>
                    {formatMoney(metricsFinancieras.porAutMxn, 'MXN')}
                  </Typography>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', color: '#b45309', fontWeight: 800, mt: 0.4 }}>
                    ✍️ Solicitudes por Autorizar
                  </Typography>
                </Box>
                <CheckBox sx={{ color: '#d97706', fontSize: 'clamp(24px, 2vw, 30px)' }} />
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 16px)', borderRadius: '10px', border: '1px solid #fecaca', bgcolor: '#fff5f5', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#991b1b', textTransform: 'uppercase' }}>PROMEDIO DE ATRASO</Typography>
                  <Typography sx={{ fontSize: 'clamp(1.1rem, 1.05rem + 0.25vw, 1.45rem)', fontWeight: 900, color: '#dc2626', mt: 0.3, lineHeight: 1.1 }}>
                    {metricsFinancieras.promAtraso} Días
                  </Typography>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', color: '#b91c1c', fontWeight: 800, mt: 0.4 }}>
                    ⏱️ Cuello de Botella Operativo
                  </Typography>
                </Box>
                <AccessTime sx={{ color: '#dc2626', fontSize: 'clamp(24px, 2vw, 30px)' }} />
              </Box>
            </Paper>

          </Box>

          {/* GRÁFICA DE FLUJO MENSUAL DE BARRAS */}
          <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', mb: 'clamp(14px, 1.8vw, 24px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a' }}>
                📊 Flujo y Salida de Dinero Mensual (Enero - Diciembre)
              </Typography>
              <TrendingUp sx={{ color: '#0284c7', fontSize: 'clamp(20px, 1.6vw, 26px)' }} />
            </Box>

            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={tendenciaMensualData} margin={{ left: 5, right: 15, top: 5, bottom: 5 }}>
                <XAxis dataKey="mesLabel" tick={{ fontSize: 10, fontWeight: 800 }} />
                <YAxis tickFormatter={formatCompactMoney} tick={{ fontSize: 9.5, fontWeight: 700 }} />
                <Tooltip formatter={(val, name) => [formatMoney(val), name === 'pendientePagar' ? 'Listo para Pago' : 'Por Autorizar (Firmas)']} />
                <Legend formatter={(value) => value === 'pendientePagar' ? 'Listo para Pago (Autorizadas)' : 'Atorado en Proceso (Firmas)'} wrapperStyle={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', fontWeight: 800 }} />
                <Bar dataKey="pendientePagar" fill="#0284c7" name="pendientePagar" radius={[3, 3, 0, 0]} />
                <Bar dataKey="porAutorizar" fill="#f59e0b" name="porAutorizar" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* SEMÁFORO DE RIESGO DE ANTIGÜEDAD */}
          <Paper elevation={0} sx={{ p: 'clamp(10px, 1.4vw, 18px)', borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontSize: 'clamp(0.75rem, 0.72rem + 0.1vw, 0.88rem)', fontWeight: 900, color: '#0f172a' }}>
                🚦 Semáforo de Riesgo y Antigüedad de Saldos (Control Morosidad)
              </Typography>
              <Warning sx={{ color: '#dc2626', fontSize: 'clamp(18px, 1.4vw, 22px)' }} />
            </Box>

            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 'clamp(4px, 0.5vw, 6px)', px: 'clamp(6px, 0.8vw, 10px)', borderBottom: '1px solid #f1f5f9' } }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>RANGO DE ATRASO</TableCell>
                  <TableCell align="center" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>CANTIDAD</TableCell>
                  <TableCell align="right" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>MONEDA NACIONAL (MXN)</TableCell>
                  <TableCell align="right" sx={{ fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)', fontWeight: 900, color: '#475569' }}>DÓLARES (USD)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {antiguedadTablaData.map((row, idx) => (
                  <TableRow key={idx} sx={{ bgcolor: row.bg }}>
                    <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: row.color }}>
                      {row.label}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', fontWeight: 900, bgcolor: '#ffffff', px: 0.8, py: 0.15, borderRadius: '5px', border: `1px solid ${row.color}`, display: 'inline-block' }}>
                        {row.count}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#0f172a' }}>
                      {formatMoney(row.mxn, 'MXN')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: row.usd > 0 ? '#0284c7' : '#94a3b8' }}>
                      {row.usd > 0 ? formatMoney(row.usd, 'USD') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

        </>
      )}
    </Box>
  );
}
